import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { fixesForGroup, type DiagnosticGroup } from '../diagnostics/group';
import type { CompileResult, Severity } from '../engine/types';
import { useI18n, type MessageKey } from '../i18n';
import type { AnalysisState } from '../lib/analysis';
import { reviewEntries } from '../lib/review';
import { Icon } from '../ui/Icon';
import { StitchRule } from '../ui/Knit';
import { CanonicalTab } from './CanonicalTab';
import { DocumentHeader } from './DocumentHeader';
import { AppliedFixes, Celebration, FindingCard, NoneUnderstood, Switch, type AppliedFix, type FindingActions } from './Findings';
import { ReviewList } from './ReviewSection';
import { RowsTab, TraceTable } from './RowsTab';
import { RowsVisual, TraceVisual } from './RowsVisual';
import { LazyShapeSection } from './shape/LazyShapeSection';
import { StitchMapTab } from './StitchMapTab';

type SectionId = 'summary' | 'fix' | 'check' | 'review' | 'rows' | 'map' | 'shape' | 'canonical';
const SECTIONS: Array<[SectionId, MessageKey]> = [
  ['summary', 'nb.summary'],
  ['fix', 'nb.toFix'],
  ['check', 'nb.toCheck'],
  ['review', 'nb.review'],
  ['rows', 'nb.rows'],
  ['map', 'nb.map'],
  ['shape', 'nb.shape'],
  ['canonical', 'nb.canonical'],
];

interface Props {
  result: CompileResult;
  analysis: AnalysisState;
  groups: DiagnosticGroup[];
  counts: Record<Severity, number>;
  fallbackTitle?: string | null;
  selectedSize: number | null;
  onSize: (s: number | null) => void;
  activeLine: number | null;
  actions: FindingActions;
  applied: AppliedFix[];
  onUndo: (a: AppliedFix) => void;
  onDismissApplied: (a: AppliedFix) => void;
  tableView: boolean;
  onTableView: (v: boolean) => void;
}

/**
 * Le carnet: the result read top to bottom like a knitting notebook, not a set of tables. A sticky
 * mini-nav jumps between the sections; everything the compact view shows is here too.
 */
export const Notebook = memo(function Notebook(props: Props) {
  const { result, analysis, groups, counts, selectedSize, onSize, activeLine, actions, applied, tableView, onTableView } = props;
  const { t } = useI18n();
  const scroller = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState<SectionId>('summary');
  const errors = useMemo(() => groups.filter((g) => g.severity === 'error'), [groups]);
  const toCheck = useMemo(() => groups.filter((g) => g.severity !== 'error'), [groups]);
  const [checkOpen, setCheckOpen] = useState<boolean | null>(null);
  const [canonicalOpen, setCanonicalOpen] = useState(false);
  const checkIsOpen = checkOpen ?? errors.length === 0;
  const fixesById = useMemo(() => new Map(result.fixes.map((f) => [f.id, f])), [result.fixes]);
  const linesByNo = useMemo(() => new Map(result.lines.map((l) => [l.line_no, l.text])), [result.lines]);
  const toReview = useMemo(() => reviewEntries(result, selectedSize), [result, selectedSize]);
  const trace = result.module_trace ?? [];
  const rowsHaveCounts = result.rows.some((r) => r.per_size.some((p) => typeof p.count_after === 'number'));

  // Scroll spy: the current section is the one whose top is nearest the bottom of the sticky nav
  // (a section scrolled past entirely is out). A click in the nav sets it at once and holds it while
  // the jump scrolls, then as long as its top is in view (near the end of the notebook the jump
  // cannot bring it up to the nav); a wheel, a touch or a key gives the hand back to the reader.
  const holdUntil = useRef(0);
  const pinned = useRef<SectionId | null>(null);
  const settle = useRef<() => void>(() => undefined);
  useEffect(() => {
    const root = scroller.current;
    if (!root) return;
    let frame = 0;
    const pick = () => {
      frame = 0;
      if (Date.now() < holdUntil.current) return;
      const nav = root.querySelector('[data-testid="notebook-nav"]');
      const line = nav ? nav.getBoundingClientRect().bottom : root.getBoundingClientRect().top;
      const pin = pinned.current ? root.querySelector(`[data-section="${pinned.current}"]`) : null;
      if (pin) {
        const top = pin.getBoundingClientRect().top;
        if (top >= line - 24 && top < root.getBoundingClientRect().bottom - 24) return;
        pinned.current = null;
      }
      let best: SectionId | null = null;
      let bestGap = Number.POSITIVE_INFINITY;
      for (const [id] of SECTIONS) {
        const el = root.querySelector(`[data-section="${id}"]`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.bottom <= line + 1) continue;
        const gap = Math.abs(r.top - line);
        if (gap < bestGap) {
          best = id;
          bestGap = gap;
        }
      }
      if (best) setCurrent(best);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(pick);
    };
    const release = () => {
      holdUntil.current = 0;
      pinned.current = null;
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    root.addEventListener('wheel', release, { passive: true });
    root.addEventListener('touchstart', release, { passive: true });
    root.addEventListener('keydown', release);
    settle.current = pick;
    pick();
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener('scroll', onScroll);
      root.removeEventListener('wheel', release);
      root.removeEventListener('touchstart', release);
      root.removeEventListener('keydown', release);
    };
  }, []);

  const go = useCallback((id: SectionId) => {
    if (id === 'check') setCheckOpen(true);
    if (id === 'canonical') setCanonicalOpen(true);
    setCurrent(id);
    pinned.current = id;
    // Held while the jump scrolls (whatever scroll events come meanwhile), then checked once.
    const HOLD_MS = 1000;
    holdUntil.current = Date.now() + HOLD_MS;
    window.setTimeout(() => settle.current(), HOLD_MS + 50);
    const el = scroller.current?.querySelector(`[data-section="${id}"]`);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }));
  }, []);

  const badge = (id: SectionId): ReactNode => {
    if (id === 'fix' && errors.length) return <span className="tabular text-error">{errors.length}</span>;
    if (id === 'check' && toCheck.length) return <span className="tabular text-warning">{toCheck.length}</span>;
    if (id === 'review' && toReview.length) return <span className="tabular text-warning">{toReview.length}</span>;
    return null;
  };

  const cards = (list: DiagnosticGroup[]) => (
    <ol className="space-y-4" data-testid="diagnostics-list">
      {list.map((g) => (
        <li key={g.key}>
          <FindingCard
            group={g}
            result={result}
            fixes={fixesForGroup(g, fixesById)}
            sourceLine={linesByNo.get(g.line_no) ?? null}
            actions={actions}
          />
        </li>
      ))}
    </ol>
  );

  return (
    <div ref={scroller} className="relative min-h-0 flex-1 overflow-y-auto scroll-pt-14" data-testid="notebook" data-print-area="notebook">
      <nav
        aria-label={t('nb.nav')}
        className="sticky top-0 z-10 border-b border-line bg-bg px-6 sm:px-10"
        data-print-hide
        data-testid="notebook-nav"
      >
        <ul className="-mx-2 flex items-center gap-1 overflow-x-auto py-2 text-xs font-semibold">
          {SECTIONS.map(([id, key]) => (
            <li key={id} className="shrink-0">
              <button
                type="button"
                onClick={() => go(id)}
                aria-current={current === id ? 'true' : undefined}
                className={`inline-flex h-8 items-center gap-1.5 rounded-pill px-3 transition-colors ${
                  current === id ? 'bg-surface text-fg' : 'text-muted hover:text-fg'
                }`}
              >
                {t(key)}
                {badge(id)}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mx-auto max-w-measure px-6 pb-24 pt-8 sm:px-10">
        <section data-section="summary" aria-label={t('nb.summary')} className="scroll-mt-16">
          <DocumentHeader
            result={result}
            analysis={analysis}
            counts={counts}
            groups={groups}
            fallbackTitle={props.fallbackTitle}
            selectedSize={selectedSize}
            onSize={onSize}
          />
        </section>

        <Divider />
        <Section id="fix" title={t('nb.toFix')} count={errors.length} tone="error">
          <div className="space-y-4">
            <AppliedFixes items={applied} onUndo={props.onUndo} onDismiss={props.onDismissApplied} />
            {analysis === 'none' ? (
              <NoneUnderstood />
            ) : groups.length === 0 ? (
              <Celebration />
            ) : errors.length > 0 ? (
              cards(errors)
            ) : (
              <p className="flex items-center gap-2 text-sm text-accent-strong">
                <Icon name="check" size={14} />
                {t('nb.toFixEmpty')}
              </p>
            )}
          </div>
        </Section>

        <Divider />
        <Section id="check" title={t('nb.toCheck')} count={toCheck.length} tone="warning">
          {toCheck.length === 0 ? (
            <p className="text-sm text-muted">{t('nb.toCheckEmpty')}</p>
          ) : errors.length > 0 ? (
            <details open={checkIsOpen} onToggle={(e) => setCheckOpen((e.currentTarget as HTMLDetailsElement).open)} className="group" data-testid="to-check">
              <summary className="inline-flex cursor-pointer list-none items-center gap-2 rounded-pill py-1 text-sm font-semibold text-muted hover:text-fg [&::-webkit-details-marker]:hidden">
                <Icon name="arrow" size={12} className="transition-transform group-open:rotate-90 motion-reduce:transition-none" />
                {checkIsOpen ? t('nb.toCheckHide') : toCheck.length === 1 ? t('nb.toCheckShow1') : t('nb.toCheckShowN', { n: toCheck.length })}
              </summary>
              <div className="mt-4">{cards(toCheck)}</div>
            </details>
          ) : (
            cards(toCheck)
          )}
        </Section>

        <Divider />
        <Section id="review" title={t('nb.reviewTitle')} count={toReview.length} tone="warning">
          <ReviewList result={result} entries={toReview} selectedSize={selectedSize} onJump={actions.onJump} />
        </Section>

        <Divider />
        <Section
          id="rows"
          title={result.rows.length === 0 && trace.length > 0 ? t('nb.stepsTitle') : t('nb.rowsTitle')}
          action={
            result.rows.length > 0 || trace.length > 0 ? (
              <Switch checked={tableView} onChange={onTableView} label={t('rows.tableView')} testId="rows-table-toggle" />
            ) : null
          }
        >
          {result.rows.length === 0 && trace.length === 0 ? (
            <p className="text-sm text-muted">{t('rows.empty')}</p>
          ) : tableView ? (
            <div className="-mx-2 overflow-x-auto rounded-card bg-surface">
              {result.rows.length > 0 ? (
                <RowsTab result={result} selectedSize={selectedSize} activeLine={activeLine} onJump={actions.onJump} />
              ) : (
                <TraceTable result={result} trace={trace} selectedSize={selectedSize} activeLine={activeLine} onJump={actions.onJump} />
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {trace.length > 0 && (
                <TraceVisual result={result} trace={trace} selectedSize={selectedSize} activeLine={activeLine} onJump={actions.onJump} />
              )}
              {result.rows.length > 0 && (rowsHaveCounts || trace.length === 0) && (
                <RowsVisual result={result} selectedSize={selectedSize} activeLine={activeLine} onJump={actions.onJump} />
              )}
            </div>
          )}
        </Section>

        <Divider />
        <Section id="map" title={t('nb.mapTitle')}>
          <StitchMapTab result={result} selectedSize={selectedSize} onSize={onSize} />
        </Section>

        <Divider />
        <Section id="shape" title={t('nb.shapeTitle')}>
          <LazyShapeSection result={result} selectedSize={selectedSize} onSize={onSize} />
        </Section>

        <Divider />
        <section data-section="canonical" aria-label={t('nb.canonical')} className="scroll-mt-16" data-print-hide>
          <details open={canonicalOpen} onToggle={(e) => setCanonicalOpen((e.currentTarget as HTMLDetailsElement).open)} className="group">
            <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
              <Icon name="arrow" size={14} className="text-muted transition-transform group-open:rotate-90 motion-reduce:transition-none" />
              <h3 className="font-display text-xl font-[440] text-fg">{t('nb.canonical')}</h3>
            </summary>
            <div className="-mx-2 mt-4 overflow-x-auto rounded-card bg-surface">{canonicalOpen && <CanonicalTab result={result} activeLine={activeLine} onJump={actions.onJump} />}</div>
          </details>
        </section>
      </div>
    </div>
  );
});

function Divider() {
  return <StitchRule className="my-10" />;
}

function Section({
  id,
  title,
  count,
  tone,
  action,
  children,
}: {
  id: SectionId;
  title: string;
  count?: number;
  tone?: 'error' | 'warning';
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section data-section={id} aria-labelledby={`nb-${id}`} className="scroll-mt-16" data-testid={`section-${id}`}>
      <div className="mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <h3 id={`nb-${id}`} className="font-display text-xl font-[440] text-fg">
          {title}
        </h3>
        {count !== undefined && count > 0 && (
          <span className={`text-sm font-bold tabular ${tone === 'error' ? 'text-error' : 'text-warning'}`}>{count}</span>
        )}
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </section>
  );
}
